import { Component, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ForgotPasswordService } from './forgot-password.service';
import { CommonModule } from '@angular/common';
import { finalize } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

type Step = 'email' | 'code' | 'reset' | 'success';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './forgot-password.html',
  styleUrls: ['./forgot-password.scss'],
})
export class ForgotPassword {
  step = signal<Step>('email');
  loading = signal(false);
  message = signal<string | null>(null);
  error = signal<string | null>(null);

  emailForm;
  codeForm;
  resetForm;

  private email = signal<string | null>(null);
  private code = signal<string | null>(null);

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private forgotService: ForgotPasswordService,
    private router: Router,
  ) {
    this.emailForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });

    this.codeForm = this.fb.group({
      code: ['', Validators.required],
    });

    this.resetForm = this.fb.group({
      password: ['', [Validators.required, Validators.minLength(6)]],
      password_confirmation: ['', Validators.required],
    });
  }

  sendEmail() {
    const email = this.emailForm.value.email;

    if (!email) {
      this.error.set('Ingresá un email válido.');
      return;
    }

    this.loading.set(true);
    this.error.set(null);
    this.message.set(null);

    this.forgotService.sendRecoveryEmail(email).subscribe({
      next: (res: { message: string }) => {
        this.snackBar.open(res.message, 'Cerrar', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['success-snackbar'],
        });

        this.email.set(email);
        this.step.set('code');
      },
      error: (err) => {
        const backendMessage =
          typeof err?.error === 'string'
            ? err.error
            : err?.error?.message || 'Error inesperado';

        this.error.set(backendMessage);

        this.snackBar.open(backendMessage, 'Cerrar', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'bottom',
          panelClass: ['error-snackbar'],
        });
      },
      complete: () => {
        this.loading.set(false);
      },
    });
  }

  validateCode() {
    if (this.codeForm.invalid) {
      this.error.set('Ingresá el código enviado a tu correo.');
      return;
    }

    const code = this.codeForm.value.code!;
    const email = this.email();

    if (!email) {
      this.error.set('Email inválido.');
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    this.forgotService.validateCode(email, code).subscribe({
      next: (res: { valid: boolean; message: string }) => {
        if (res.valid) {
          this.code.set(code);
          this.message.set(res.message);
          this.step.set('reset');
        } else {
          this.error.set(res.message);
        }
      },
      error: (err) => {
        this.error.set(
          typeof err?.error === 'string'
            ? err.error
            : err?.error?.message || 'Código inválido o expirado.',
        );
      },
      complete: () => {
        this.loading.set(false);
      },
    });
  }

  resetPassword() {
    if (this.resetForm.invalid) {
      this.error.set('Completá ambos campos correctamente.');
      return;
    }

    const email = this.email();
    const code = this.code();

    if (!email || !code) {
      this.error.set('Datos de recuperación inválidos.');
      return;
    }

    const { password, password_confirmation } = this.resetForm.value;

    this.loading.set(true);
    this.error.set(null);

    this.forgotService
      .resetPassword({
        email,
        code,
        password: password!,
        password_confirmation: password_confirmation!,
      })
      .subscribe({
        next: (res: { message: string }) => {
          this.message.set(res.message);
          this.step.set('success');
        },
        error: (err) => {
          this.error.set(
            typeof err?.error === 'string'
              ? err.error
              : err?.error?.message || 'No se pudo actualizar la contraseña.',
          );
        },
        complete: () => {
          this.loading.set(false);
        },
      });
  }

  gotoLogin() {
    this.router.navigate(['/login']);
  }

  openEmailApp() {
    const emailProviders = [
      'mailto:',
      'googlegmail://',
      'ms-outlook://',
      'ymail://',
      'readdle-spark://',
    ];

    for (const provider of emailProviders) {
      window.open(provider, '_blank');
    }
  }
}
